import { ApplicationRef, EnvironmentInjector, Injectable, NgZone, createComponent } from '@angular/core';

import { PhoneUploadPanelComponent } from './phone-upload-panel/phone-upload-panel.component';
import { PhoneUploadContext, PhoneUploadFile } from './phone-upload.models';
import { createPhoneUploadPlugin, UploaderPlugin } from './phone-upload.plugin';

/**
 * Renders <phone-upload-panel> into DOM the uploader owns (its activity area),
 * outside any Angular template.
 */
@Injectable({ providedIn: 'root' })
export class PhoneUploadPanelMounter {

  constructor(private appRef: ApplicationRef, private injector: EnvironmentInjector, private zone: NgZone) {}

  public mount(host: HTMLElement, context: PhoneUploadContext, onFiles: (files: PhoneUploadFile[]) => void): () => void {
    // The uploader calls this after a native `await`, which drops Angular's zone. Without
    // zone.run the panel's requests and timers don't trigger change detection, so the
    // QR code only appears when something else on the page redraws.
    return this.zone.run(() => {
      const ref = createComponent(PhoneUploadPanelComponent, { environmentInjector: this.injector });
      ref.setInput('context', context);
      const subscription = ref.instance.filesReceived.subscribe(onFiles);
      this.appRef.attachView(ref.hostView);
      host.appendChild(ref.location.nativeElement);
      ref.changeDetectorRef.detectChanges();

      return () => this.zone.run(() => {
        subscription.unsubscribe();
        this.appRef.detachView(ref.hostView);
        ref.destroy();
        host.replaceChildren();
      });
    });
  }

  /** The Uploadcare plugin, wired to this mounter. `getContext` is read each time the source opens. */
  public createPlugin(getContext: () => PhoneUploadContext): UploaderPlugin {
    return createPhoneUploadPlugin({
      mountPanel: (host, onFiles) => this.mount(host, getContext(), onFiles)
    });
  }
}
